<template>
  <div class="auth__wrapper">
    <EmailNotVerified v-if="submittedEmail" :email="submittedEmail" />
    <template v-else>
      <div class="auth__logo">
        <nuxt-link :to="{ name: 'index' }"><Logo /></nuxt-link>
      </div>
      <h1 class="auth__head-title">{{ $t('freeTrial.title') }}</h1>
      <div class="auth__head">
        <span class="auth__head-text">
          {{ $t('freeTrial.loginText') }}
          <nuxt-link :to="{ name: 'login' }">{{
            $t('action.login')
          }}</nuxt-link>
        </span>
        <LangPicker />
      </div>

      <Alert v-if="!availability?.open" type="error">
        <template #title>{{ $t('freeTrial.closedTitle') }}</template>
        <p>{{ $t('freeTrial.closedDescription') }}</p>
      </Alert>

      <form v-else @submit.prevent="submit">
        <Alert v-if="formError" type="error">
          <template #title>{{ $t('freeTrial.errorTitle') }}</template>
          <p>{{ formError }}</p>
        </Alert>
        <FormGroup small-label :label="$t('field.name')" required class="mb-24">
          <FormInput
            v-model="form.name"
            size="large"
            required
            autocomplete="name"
          />
        </FormGroup>
        <FormGroup
          small-label
          :label="$t('freeTrial.company')"
          required
          class="mb-24"
        >
          <FormInput
            v-model="form.company"
            size="large"
            required
            autocomplete="organization"
          />
        </FormGroup>
        <FormGroup
          small-label
          :label="$t('field.emailAddress')"
          required
          class="mb-24"
        >
          <FormInput
            v-model="form.email"
            size="large"
            type="email"
            required
            autocomplete="email"
          />
        </FormGroup>
        <FormGroup
          small-label
          :label="$t('freeTrial.whatsapp')"
          required
          class="mb-24"
        >
          <FormInput
            v-model="form.whatsapp"
            size="large"
            required
            autocomplete="tel"
            placeholder="(11) 99999-9999"
          />
        </FormGroup>
        <FormGroup
          small-label
          :label="$t('field.password')"
          required
          class="mb-24"
        >
          <FormInput
            v-model="form.password"
            size="large"
            type="password"
            required
            autocomplete="new-password"
          />
        </FormGroup>
        <CaptchaWidget
          ref="captchaWidget"
          context="signup"
          action="canis_trial_signup"
          @token="onCaptchaToken"
        />
        <p class="auth__head-text mb-24">
          {{ $t('freeTrial.privacyPrefix') }}
          <a
            :href="availability.privacy_policy_url"
            target="_blank"
            rel="noopener"
            >{{ $t('freeTrial.privacyPolicy') }}</a
          >
          {{ $t('freeTrial.privacyAnd') }}
          <a :href="availability.terms_url" target="_blank" rel="noopener">{{
            $t('freeTrial.terms')
          }}</a
          >.
        </p>
        <div class="auth__action mt-32 mb-32">
          <Button
            type="primary"
            size="large"
            :loading="loading"
            :disabled="loading || !captchaToken"
            full-width
          >
            {{ $t('freeTrial.submit') }}
          </Button>
        </div>
      </form>
    </template>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import LangPicker from '@baserow/modules/core/components/LangPicker'
import CaptchaWidget from '@baserow/modules/core/components/auth/CaptchaWidget'
import EmailNotVerified from '@baserow/modules/core/components/auth/EmailNotVerified.vue'
import SubscriptionService from '@baserow/modules/core/services/subscription'

definePageMeta({
  layout: 'login',
  middleware: ['settings'],
})

const { $client } = useNuxtApp()
const { t } = useI18n()
const form = reactive({
  name: '',
  company: '',
  email: '',
  whatsapp: '',
  password: '',
})
const loading = ref(false)
const formError = ref('')
const captchaWidget = ref(null)
const captchaToken = ref('')
const submittedEmail = ref('')

const { data: availability } = await useAsyncData(
  'canis-trial-availability',
  async () => {
    try {
      const { data } = await SubscriptionService($client).getTrialAvailability()
      return data
    } catch {
      return { open: false }
    }
  }
)

const onCaptchaToken = (token) => {
  captchaToken.value = token
}

const resetCaptcha = () => {
  captchaWidget.value?.reset()
  captchaToken.value = ''
}

const submit = async () => {
  if (!captchaToken.value) {
    formError.value = t('error.captchaVerificationFailedMessage')
    return
  }

  loading.value = true
  formError.value = ''
  try {
    await SubscriptionService($client).requestTrial({
      ...form,
      captcha_token: captchaToken.value,
    })
    submittedEmail.value = form.email
  } catch (error) {
    resetCaptcha()
    const errorCode = error.response?.data?.error
    formError.value =
      errorCode === 'ERROR_CAPTCHA_VERIFICATION_FAILED'
        ? t('error.captchaVerificationFailedMessage')
        : errorCode === 'ERROR_CAPTCHA_SERVICE_UNAVAILABLE'
          ? t('freeTrial.captchaUnavailableDescription')
          : errorCode === 'ERROR_TRIAL_PROVISIONING_UNAVAILABLE'
            ? t('freeTrial.provisioningUnavailableDescription')
            : error.response?.data?.detail || t('freeTrial.errorDescription')
  } finally {
    loading.value = false
  }
}

useHead({ title: t('freeTrial.title') })
</script>
